/**
 * Analyse 3 voies d'une fusion de branches : pour chaque entité, compare son état
 * dans la BASE (origine commune), la CIBLE et la branche FUSIONNÉE, et décide :
 *
 * | Cible \ Fusionnée  | inchangée | modifiée        | supprimée       |
 * |--------------------|-----------|-----------------|-----------------|
 * | inchangée          | cible     | fusionnée       | supprimer       |
 * | modifiée           | cible     | CONFLIT (≠)     | CONFLIT         |
 * | supprimée          | absente   | CONFLIT         | absente         |
 *
 * Le rattachement à une observation est un champ à part (un inspecteur déplace un
 * trait, l'autre le rattache : pas de conflit) ; il compte toutefois comme une
 * modification face à une suppression (on ne perd pas un rattachement en silence).
 *
 * Les ajouts depuis le fork ne se confondent pas : ceux de la cible restent, ceux
 * de la branche fusionnée sont copiés. Seule exception, le MÊME ajout présent des
 * deux côtés (cf. `withoutSharedAdditions`). Les dates de l'oplog ne servent JAMAIS
 * à départager (horloges des tablettes non fiables).
 *
 * Les n° de marqueurs photo ne sont jamais changés : un même n° peut désigner une
 * même photo ou repartir de 1 par partie d'ouvrage, la fusion ne peut pas trancher.
 */

import type {
  DrawingSnapshot,
  EntityDecision,
  EntitySnapshot,
  MergeAnalysis,
  MergeConflict,
  MergeSummary,
  NamedRecordSnapshot,
  RecordAction,
} from "./types";

export const entityConflictId = (baseHandle: string): string =>
  `entity:${baseHandle}`;
export const observationConflictId = (baseHandle: string): string =>
  `observation:${baseHandle}`;

/** uids portés par une seule entité (les doublons — copies d'avant correctif — sont ambigus). */
export function uniqueUids(
  entities: EntitySnapshot[],
): Map<string, EntitySnapshot> {
  const byUid = new Map<string, EntitySnapshot>();
  const dup = new Set<string>();
  for (const e of entities) {
    if (!e.uid) continue;
    if (byUid.has(e.uid)) dup.add(e.uid);
    else byUid.set(e.uid, e);
  }
  for (const uid of dup) byUid.delete(uid);
  return byUid;
}

/**
 * Même handle, mais deux entités : un ajout copié par une fusion change de handle,
 * et l'ancien peut alors désigner un ajout de l'autre version. Deux uids différents
 * sur un même contenu restent une seule entité : le fond de plan a pu recevoir un
 * uid de chaque côté, et une fusion n'en garde qu'un.
 */
function isOtherEntity(a: EntitySnapshot, b: EntitySnapshot): boolean {
  return (
    a.uid !== null &&
    b.uid !== null &&
    a.uid !== b.uid &&
    a.fingerprint !== b.fingerprint
  );
}

const contentKey = (e: EntitySnapshot): string =>
  `${e.className}|${e.fingerprint}`;

function groupByContent(
  entities: EntitySnapshot[],
): Map<string, EntitySnapshot[]> {
  const groups = new Map<string, EntitySnapshot[]>();
  for (const e of entities) {
    const list = groups.get(contentKey(e));
    if (list) list.push(e);
    else groups.set(contentKey(e), [e]);
  }
  return groups;
}

/** Le même uid porté par plusieurs entités de la base : il ne désigne plus aucune d'elles. */
const hasAmbiguousUid = (
  b: EntitySnapshot,
  baseByUid: Map<string, EntitySnapshot>,
): boolean => b.uid !== null && !baseByUid.has(b.uid);

/**
 * Apparie les entités d'un côté à celles de la base, indexées par HANDLE DE BASE :
 *  1. par uid unique des deux côtés (même classe) ;
 *  2. par handle (même classe), pour les entités sans uid, comme le fond de plan
 *     d'origine, et celles dont l'uid est ambigu ;
 *  3. par contenu, pour une entité de la base à l'uid ambigu que sa copie par une
 *     fusion a fait changer de handle. Deux entités ainsi confondues ont le même
 *     contenu : elles sont interchangeables.
 * Le reste est un ajout propre à ce côté.
 */
export function indexAgainstBase(
  side: EntitySnapshot[],
  baseByHandle: Map<string, EntitySnapshot>,
  baseByUid: Map<string, EntitySnapshot>,
): { existing: Map<string, EntitySnapshot>; added: EntitySnapshot[] } {
  const existing = new Map<string, EntitySnapshot>();
  const unmatched: EntitySnapshot[] = [];
  const sideUids = uniqueUids(side);
  for (const e of side) {
    const b =
      e.uid && sideUids.get(e.uid) === e ? baseByUid.get(e.uid) : undefined;
    if (b && b.className === e.className) existing.set(b.handle, e);
    else unmatched.push(e);
  }
  const added: EntitySnapshot[] = [];
  for (const e of unmatched) {
    const b = baseByHandle.get(e.handle);
    if (
      b &&
      b.className === e.className &&
      !existing.has(b.handle) &&
      !isOtherEntity(b, e)
    ) {
      existing.set(b.handle, e);
    } else added.push(e);
  }

  const orphans = [...baseByHandle.values()].filter(
    (b) => hasAmbiguousUid(b, baseByUid) && !existing.has(b.handle),
  );
  if (!orphans.length) return { existing, added };
  const addedByContent = groupByContent(added);
  const paired = new Set<EntitySnapshot>();
  for (const b of orphans) {
    const e = addedByContent.get(contentKey(b))?.shift();
    if (e) {
      existing.set(b.handle, e);
      paired.add(e);
    }
  }
  return { existing, added: added.filter((e) => !paired.has(e)) };
}

/**
 * Même ajout des deux côtés : même identité (uid, ou handle pour deux entités sans
 * uid) ET même contenu, rattachement compris. Deux ajouts indépendants qui ont
 * reçu le même handle ont un contenu différent et restent distincts.
 */
function isSameAddition(a: EntitySnapshot, b: EntitySnapshot): boolean {
  return (
    a.uid === b.uid &&
    (a.uid !== null || a.handle === b.handle) &&
    a.className === b.className &&
    a.fingerprint === b.fingerprint &&
    a.observationId === b.observationId
  );
}

/**
 * Ajouts de la fusionnée, hors ceux que la cible a déjà (une seule entité au résultat).
 * Une copie à l'uid ambigu reçoit un uid neuf en entrant dans une cible : d'un côté
 * ou de l'autre, seul le contenu permet alors de reconnaître le même ajout.
 */
export function withoutSharedAdditions(
  targetAdded: EntitySnapshot[],
  mergedAdded: EntitySnapshot[],
): EntitySnapshot[] {
  const byHandle = new Map(targetAdded.map((e) => [e.handle, e]));
  const byUid = uniqueUids(targetAdded);
  const taken = new Set<EntitySnapshot>();
  const remaining = mergedAdded.filter((e) => {
    const t = (e.uid ? byUid.get(e.uid) : undefined) ?? byHandle.get(e.handle);
    if (!t || taken.has(t) || !isSameAddition(t, e)) return true;
    taken.add(t);
    return false;
  });

  const mergedUids = uniqueUids(mergedAdded);
  const targetByContent = groupByContent(targetAdded);
  const isAmbiguous = (
    e: EntitySnapshot,
    unique: Map<string, EntitySnapshot>,
  ): boolean => e.uid !== null && !unique.has(e.uid);
  return remaining.filter((e) => {
    const t = targetByContent
      .get(contentKey(e))
      ?.find(
        (c) =>
          !taken.has(c) &&
          c.observationId === e.observationId &&
          (isAmbiguous(e, mergedUids) || isAmbiguous(c, byUid)),
      );
    if (!t) return true;
    taken.add(t);
    return false;
  });
}

/** N° de marqueurs ajoutés des deux côtés depuis le fork, triés (ceux de la base sont communs). */
function sharedMarkerNumbers(
  base: EntitySnapshot[],
  targetAdded: EntitySnapshot[],
  mergedAdded: EntitySnapshot[],
): number[] {
  const numbers = (entities: EntitySnapshot[]) =>
    new Set(
      entities.flatMap((e) =>
        e.markerNumber === null ? [] : [e.markerNumber],
      ),
    );
  const inBase = numbers(base);
  const inTarget = numbers(targetAdded);
  return [...numbers(mergedAdded)]
    .filter((n) => inTarget.has(n) && !inBase.has(n))
    .sort((a, b) => a - b);
}

/**
 * Au-delà de cette part d'entités de la base RÉAPPARUES sous un autre handle d'un
 * côté (sur un plan d'au moins IDENTITY_MIN_BASE entités), l'appariement est jugé
 * non fiable : les handles de ce DWG ont été renumérotés, et la fusion ferait
 * passer tout le fond de plan pour « supprimé puis ajouté ». Elle est refusée.
 *
 * On compte les réapparitions et non les disparitions : une branche qui a
 * réellement supprimé l'essentiel du plan (fond de plan remplacé) n'a pas
 * d'équivalent parmi ses ajouts et reste fusionnable.
 */
export const IDENTITY_MAX_REAPPEARED_RATIO = 0.5;
const IDENTITY_MIN_BASE = 20;

/** Entités de la base absentes d'un côté dont un ajout de ce côté est la copie conforme. */
function countReappeared(
  base: EntitySnapshot[],
  side: { existing: Map<string, EntitySnapshot>; added: EntitySnapshot[] },
): number {
  const addedByKey = new Map<string, number>();
  for (const e of side.added) {
    const key = contentKey(e);
    addedByKey.set(key, (addedByKey.get(key) ?? 0) + 1);
  }
  let reappeared = 0;
  for (const b of base) {
    if (side.existing.has(b.handle)) continue;
    const left = addedByKey.get(contentKey(b)) ?? 0;
    if (left > 0) {
      addedByKey.set(contentKey(b), left - 1);
      reappeared++;
    }
  }
  return reappeared;
}

/**
 * Fusion 3 voies d'une valeur scalaire. `undefined` = conflit (les deux côtés ont
 * changé différemment).
 */
function mergeValue<T>(base: T, target: T, merged: T): T | undefined {
  if (target === merged) return target;
  if (merged === base) return target;
  if (target === base) return merged;
  return undefined;
}

function decideEntity(
  b: EntitySnapshot,
  t: EntitySnapshot | null,
  m: EntitySnapshot | null,
  conflicts: MergeConflict[],
  summary: MergeSummary,
): EntityDecision | null {
  const tChanged = !!t && t.fingerprint !== b.fingerprint;
  const mChanged = !!m && m.fingerprint !== b.fingerprint;
  const tTouched = tChanged || (!!t && t.observationId !== b.observationId);
  const mTouched = mChanged || (!!m && m.observationId !== b.observationId);

  const conflict = (kind: MergeConflict["kind"]): { conflictId: string } => {
    const id = entityConflictId(b.handle);
    conflicts.push({ id, kind, base: b, target: t, merged: m });
    return { conflictId: id };
  };

  if (!t && !m) return null;

  if (t && !m) {
    if (tTouched) {
      return {
        base: b,
        target: t,
        merged: null,
        geometry: conflict("modify-delete"),
        observation: t.observationId,
      };
    }
    summary.deleted++;
    return {
      base: b,
      target: t,
      merged: null,
      geometry: "delete",
      observation: null,
    };
  }

  if (!t && m) {
    if (mTouched) {
      return {
        base: b,
        target: null,
        merged: m,
        geometry: conflict("delete-modify"),
        observation: m.observationId,
      };
    }
    return {
      base: b,
      target: null,
      merged: m,
      geometry: "absent",
      observation: null,
    };
  }

  // Présente des deux côtés.
  const tt = t as EntitySnapshot;
  const mm = m as EntitySnapshot;
  let geometry: EntityDecision["geometry"];
  if (!mChanged || mm.fingerprint === tt.fingerprint) geometry = "target";
  else if (!tChanged) {
    geometry = "merged";
    summary.modified++;
  } else geometry = conflict("modify-modify");

  let observation: EntityDecision["observation"];
  const obs = mergeValue(b.observationId, tt.observationId, mm.observationId);
  if (obs === undefined) {
    const id = observationConflictId(b.handle);
    conflicts.push({
      id,
      kind: "observation",
      base: b,
      target: tt,
      merged: mm,
    });
    observation = { conflictId: id };
  } else {
    observation = obs;
    if (obs !== tt.observationId) summary.observationLinks++;
  }

  return { base: b, target: tt, merged: mm, geometry, observation };
}

/**
 * Fusion 3 voies des éléments nommés, par sorte puis par nom. Jamais de conflit :
 * l'utilisateur ne tranche que les entités.
 *  - modifié (ou ajouté sous le même nom) des deux côtés → la CIBLE gagne ;
 *  - modifié d'un côté, supprimé de l'autre → la version MODIFIÉE est gardée ;
 *  - modifié ou ajouté d'un seul côté → repris de ce côté ;
 *  - supprimé d'un seul côté (l'autre inchangé) → supprimé pour une présentation
 *    seulement : un style, un type de ligne ou un bloc peut encore être
 *    référencé par une entité de l'autre branche, il est gardé.
 */
function decideRecords(
  base: NamedRecordSnapshot[],
  target: NamedRecordSnapshot[],
  merged: NamedRecordSnapshot[],
): RecordAction[] {
  const key = (r: NamedRecordSnapshot) => `${r.kind}:${r.name}`;
  const index = (records: NamedRecordSnapshot[]) =>
    new Map(records.map((r) => [key(r), r]));
  const b = index(base);
  const t = index(target);
  const m = index(merged);

  const actions: RecordAction[] = [];
  const copy = (r: NamedRecordSnapshot) =>
    actions.push({ kind: r.kind, name: r.name, action: "copy" });

  for (const k of new Set([...b.keys(), ...t.keys(), ...m.keys()])) {
    const rb = b.get(k);
    const rt = t.get(k);
    const rm = m.get(k);

    if (!rm) {
      // Supprimé dans la branche fusionnée : propagé si la cible n'y a pas touché.
      if (
        rb &&
        rt &&
        rt.kind === "layout" &&
        rt.fingerprint === rb.fingerprint
      ) {
        actions.push({ kind: rt.kind, name: rt.name, action: "delete" });
      }
      continue;
    }
    if (!rt) {
      // Ajouté dans la fusionnée, ou supprimé dans la cible mais modifié en face.
      if (!rb || rm.fingerprint !== rb.fingerprint) copy(rm);
      continue;
    }
    // Présent des deux côtés : repris seulement si la cible n'a rien changé.
    const mChanged = !rb || rm.fingerprint !== rb.fingerprint;
    const tChanged = !rb || rt.fingerprint !== rb.fingerprint;
    if (mChanged && !tChanged) copy(rm);
  }
  return actions;
}

/** Analyse la fusion de `merged` dans `target`, depuis leur origine commune `base`. */
export function analyzeMerge(
  base: DrawingSnapshot,
  target: DrawingSnapshot,
  merged: DrawingSnapshot,
): MergeAnalysis {
  const baseByHandle = new Map(base.entities.map((e) => [e.handle, e]));
  const baseByUid = uniqueUids(base.entities);
  const t = indexAgainstBase(target.entities, baseByHandle, baseByUid);
  const m = indexAgainstBase(merged.entities, baseByHandle, baseByUid);

  const baseCount = base.entities.length;
  const ratio = (n: number, empty: number) =>
    baseCount ? n / baseCount : empty;
  const matchRatio = {
    target: ratio(t.existing.size, 1),
    merged: ratio(m.existing.size, 1),
  };
  const reappearedRatio = {
    target: ratio(countReappeared(base.entities, t), 0),
    merged: ratio(countReappeared(base.entities, m), 0),
  };
  const identityMismatch =
    baseCount >= IDENTITY_MIN_BASE &&
    Math.max(reappearedRatio.target, reappearedRatio.merged) >
      IDENTITY_MAX_REAPPEARED_RATIO;

  const addedInMerged = withoutSharedAdditions(t.added, m.added);
  const conflicts: MergeConflict[] = [];
  const summary: MergeSummary = {
    added: addedInMerged.length,
    modified: 0,
    deleted: 0,
    observationLinks: 0,
    records: {},
    conflicts: 0,
  };

  const decisions: EntityDecision[] = [];
  for (const b of base.entities) {
    const decision = decideEntity(
      b,
      t.existing.get(b.handle) ?? null,
      m.existing.get(b.handle) ?? null,
      conflicts,
      summary,
    );
    if (decision) {
      if (hasAmbiguousUid(b, baseByUid)) decision.ambiguousUid = true;
      decisions.push(decision);
    }
  }

  const recordActions = decideRecords(
    base.records,
    target.records,
    merged.records,
  );
  for (const a of recordActions) {
    summary.records[a.kind] = (summary.records[a.kind] ?? 0) + 1;
  }
  summary.conflicts = conflicts.length;

  return {
    decisions,
    addedInTarget: t.added,
    addedInMerged,
    sharedMarkerNumbers: sharedMarkerNumbers(
      base.entities,
      t.added,
      addedInMerged,
    ),
    recordActions,
    conflicts,
    summary,
    matchRatio,
    reappearedRatio,
    identityMismatch,
  };
}
